import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTimer } from "react-timer-hook";
import { Dropdown } from "@components";
import { useBodyScrollLock } from "@hooks";
import { useRecaptchaDismissalDetector } from "../../hooks/useRecaptchaDismissalDetector";
import { useSendOTPWithRecaptcha, useVerifyOTPMutation } from "../../store/auth/otp.api";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { useSharedContent } from "../content";
import { RegistrationShell } from "./components/RegistrationShell";
import { RegistrationIcon } from "./components/RegistrationIcon";
import { OtpDigitInput } from "./components/OtpDigitInput";
import { useGeniusRegistration } from "./GeniusRegistrationContext";
import { useGeniusGeo } from "./useGeniusGeo";
import { useGeniusLeadCapture } from "./useGeniusLeadCapture";
import { INDIA_ISD } from "./constants";
import { validateEmailOptional, validateMobile, validateName } from "./validations";
import { downloadBrochurePdf } from "./downloadBrochure";
import styles from "./GeniusRegistrationModal.module.css";

const BROCHURE_STEPS = {
  FORM: "form",
  OTP: "otp",
  SUCCESS: "success"
};

const OTP_TIMER_SEC = 30;
const RECAPTCHA_BODY_CLASS = "genius-recaptcha-active";

const setRecaptchaActive = (active) => {
  document.body.classList.toggle(RECAPTCHA_BODY_CLASS, active);
};

const sanitizeNameInput = (value) =>
  value
    .replace(/[^a-zA-Z\s]/g, "")
    .replace(/^\s+/, "")
    .replace(/\s{2,}/g, " ");

const initialFormState = () => ({
  firstName: "",
  lastName: "",
  role: "",
  schoolName: "",
  stateName: "",
  city: "",
  email: "",
  phone: ""
});

export const GeniusBrochureModal = () => {
  const { isBrochureOpen, closeBrochure } = useGeniusRegistration();
  const brochureConfig = useSharedContent("brochureDownload");
  const modalCopy = brochureConfig.modal ?? {};
  const validationCopy = brochureConfig.validation ?? {};
  const messages = brochureConfig.messages ?? {};

  const { states, cities, loadingStates, loadingCities, loadCitiesForState } = useGeniusGeo();
  const { submitBrochureLead } = useGeniusLeadCapture();
  const { trackGeniusBrochureDownload } = useAnalytics();

  const [step, setStep] = useState(BROCHURE_STEPS.FORM);
  const [form, setForm] = useState(initialFormState);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle");
  const sendingOtpRef = useRef(false);

  const [sendOTP] = useSendOTPWithRecaptcha();
  const [verifyOTP] = useVerifyOTPMutation();

  const { restart, seconds, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {}
  });

  const { setupDismissalListener, cleanup } = useRecaptchaDismissalDetector({
    onDismissed: () => {
      setRecaptchaActive(false);
      setLoading(false);
      if (!sendingOtpRef.current) return;
      setFormError(messages.otpSendError || "Security verification cancelled. Please try again.");
    }
  });

  useBodyScrollLock(isBrochureOpen);

  const resetAll = useCallback(() => {
    setStep(BROCHURE_STEPS.FORM);
    setForm(initialFormState());
    setFieldErrors({});
    setFormError("");
    setEmailTouched(false);
    setOtp("");
    setOtpError("");
    setLoading(false);
    setStatus("idle");
    sendingOtpRef.current = false;
  }, []);

  const prevBrochureOpenRef = useRef(false);

  useEffect(() => {
    if (isBrochureOpen && !prevBrochureOpenRef.current) {
      resetAll();
    }
    prevBrochureOpenRef.current = isBrochureOpen;
  }, [isBrochureOpen, resetAll]);

  useEffect(
    () => () => {
      setRecaptchaActive(false);
      cleanup();
    },
    [cleanup]
  );

  const roleOptions = useMemo(() => {
    const roleField = brochureConfig.form?.fields?.find((field) => field.id === "role");
    return (roleField?.options ?? []).map((option) => ({
      id: option.id,
      name: option.label,
      value: option.value
    }));
  }, [brochureConfig.form?.fields]);

  const formFieldsById = useMemo(() => {
    const map = {};
    (brochureConfig.form?.fields ?? []).forEach((field) => {
      map[field.id] = field;
    });
    return map;
  }, [brochureConfig.form?.fields]);

  const fieldLabel = (id, fallback) => formFieldsById[id]?.label ?? fallback;
  const fieldPlaceholder = (id, fallback) => formFieldsById[id]?.placeholder ?? fallback;
  const fieldRequired = (id, fallback = false) => formFieldsById[id]?.required ?? fallback;
  const phoneIsd = formFieldsById.phone?.isdCode ?? INDIA_ISD;

  const handleClose = () => {
    closeBrochure();
    resetAll();
  };

  const handleStateChange = (val) => {
    setForm((prev) => ({ ...prev, stateName: val, city: "" }));
    loadCitiesForState(val);
  };

  const validateForm = () => {
    const errors = {};
    const firstNameResult = validateName(form.firstName, { required: true });
    const lastNameResult = validateName(form.lastName, { required: false });

    if (!firstNameResult.valid) {
      errors.firstName = validationCopy.firstNameRequired || firstNameResult.message;
    } else if (firstNameResult.normalized.length < 2) {
      errors.firstName = validationCopy.firstNameMinLength || "First name must be at least 2 characters";
    }

    if (lastNameResult.normalized && !lastNameResult.valid) {
      errors.lastName = validationCopy.lastNameInvalid || lastNameResult.message;
    }

    if (!form.role) {
      errors.role = validationCopy.roleRequired || "Please select your role";
    }

    if (!form.schoolName.trim()) {
      errors.schoolName = validationCopy.schoolNameRequired || "School name is required";
    } else if (form.schoolName.trim().length < 2) {
      errors.schoolName = validationCopy.schoolNameMinLength || "School name must be at least 2 characters";
    }

    if (!form.stateName) {
      errors.state = validationCopy.stateRequired || "Please select state";
    }

    if (!form.city) {
      errors.city = validationCopy.cityRequired || "Please select city";
    }

    const emailResult = validateEmailOptional(form.email);
    if (!emailResult.valid) {
      errors.email = validationCopy.emailInvalid || emailResult.message;
    }

    const phoneResult = validateMobile(form.phone);
    if (!phoneResult.valid) {
      errors.phone = validationCopy.phoneLength || phoneResult.message;
    }

    setFieldErrors(errors);
    return { errors, firstNameResult, lastNameResult, emailResult, phoneResult };
  };

  const isFormValid = useMemo(() => {
    const firstNameResult = validateName(form.firstName, { required: true });
    const lastNameResult = validateName(form.lastName, { required: false });
    const emailResult = validateEmailOptional(form.email);
    const phoneResult = validateMobile(form.phone);

    return (
      firstNameResult.valid &&
      firstNameResult.normalized.length >= 2 &&
      lastNameResult.valid &&
      Boolean(form.role) &&
      form.schoolName.trim().length > 1 &&
      Boolean(form.stateName) &&
      Boolean(form.city) &&
      emailResult.valid &&
      phoneResult.valid
    );
  }, [form]);

  const startTimer = useCallback(() => {
    const time = new Date();
    time.setSeconds(time.getSeconds() + OTP_TIMER_SEC);
    restart(time);
  }, [restart]);

  const sendOtpToPhone = async () => {
    const { errors, phoneResult } = validateForm();
    if (Object.keys(errors).length) return false;

    setLoading(true);
    setFormError("");
    setOtpError("");
    sendingOtpRef.current = true;
    setupDismissalListener();
    setRecaptchaActive(true);

    try {
      await sendOTP({ phoneNumber: phoneResult.normalized, isdCode: INDIA_ISD });
      startTimer();
      setStep(BROCHURE_STEPS.OTP);
      setOtp("");
      return true;
    } catch (error) {
      const err = error?.error?.data || error?.error || error;
      const message =
        err?.message ||
        err?.detail ||
        (typeof err === "string" ? err : messages.otpSendError || "Failed to send OTP. Please try again.");

      setFormError(
        message.toLowerCase().includes("recaptcha") || message.toLowerCase().includes("security verification")
          ? "Security verification failed. Complete the reCAPTCHA check and try again."
          : message
      );
      return false;
    } finally {
      sendingOtpRef.current = false;
      setRecaptchaActive(false);
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    await sendOtpToPhone();
  };

  const handleResend = async () => {
    if (isRunning || loading) return;
    await sendOtpToPhone();
  };

  const handleEditPhone = () => {
    setOtp("");
    setOtpError("");
    setFormError("");
    setStep(BROCHURE_STEPS.FORM);
  };

  const handleVerifyAndDownload = async () => {
    if (otp.length !== 4) {
      setOtpError(messages.otpInvalid || "Enter the 4-digit OTP");
      return;
    }

    const { firstNameResult, lastNameResult, emailResult, phoneResult } = validateForm();
    if (!phoneResult.valid) return;

    setLoading(true);
    setOtpError("");
    setFormError("");

    try {
      const result = await verifyOTP({
        phoneNumber: phoneResult.normalized,
        isdCode: INDIA_ISD,
        otpCode: otp
      });

      if (result?.error) {
        setOtpError(result.error?.data?.message || messages.otpInvalid || "Incorrect OTP. Please try again.");
        return;
      }

      if (!result?.data) {
        setOtpError(messages.otpInvalid || "Incorrect OTP. Please try again.");
        return;
      }

      const lsqResult = await submitBrochureLead({
        phone: phoneResult.normalized,
        firstName: firstNameResult.normalized,
        lastName: lastNameResult.normalized,
        email: emailResult.normalized,
        role: form.role,
        school: form.schoolName.trim(),
        state: form.stateName,
        activityCode: brochureConfig.lsq?.activityCode
      });

      if (!lsqResult.success) {
        setFormError(messages.formError || "Could not submit your details. Please try again.");
        return;
      }

      trackGeniusBrochureDownload({
        email: emailResult.normalized || undefined,
        phone: phoneResult.normalized
      });
      downloadBrochurePdf(brochureConfig.pdf);
      setOtp("");
      setOtpError("");
      setFormError("");
      setStep(BROCHURE_STEPS.SUCCESS);
      setStatus("success");
      setTimeout(handleClose, 2500);
    } catch {
      setFormError(messages.downloadError || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isBrochureOpen || brochureConfig.enabled === false) return null;

  const isOtpStep = step === BROCHURE_STEPS.OTP;
  const isSuccessStep = step === BROCHURE_STEPS.SUCCESS;
  const title = isSuccessStep
    ? modalCopy.successTitle || modalCopy.title || "Download brochure"
    : modalCopy.title || "Download brochure";
  const successMessage = modalCopy.successMessage || "Download started....!";

  return createPortal(
    <div
      className={`${styles.backdrop} ${styles.geniusRegModal}`}
      role="presentation"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) handleClose();
      }}
    >
      <RegistrationShell title={title} onClose={handleClose} wide centerTitle>
        {isSuccessStep ? (
          <div className={`${styles.body} ${styles.brochureSuccessBody}`}>
            <p className={styles.brochureSuccessMessage}>{successMessage}</p>
          </div>
        ) : isOtpStep ? (
          <div className={styles.body}>
            <div className={styles.field}>
              <label className={styles.label}>Phone number</label>
              <div className={styles.phoneLockedBar}>
                <span>
                  {INDIA_ISD} {form.phone}
                </span>
                <button type="button" className={styles.editPhoneBtn} onClick={handleEditPhone} disabled={loading}>
                  <RegistrationIcon name="editPhone" className={styles.editPhoneIcon} fallback={() => null} />
                  Edit
                </button>
              </div>
            </div>

            <p className={styles.otpSectionLabel}>
              {modalCopy.otpSectionLabel || "Enter the OTP sent to your mobile number"}
            </p>
            <div className={styles.otpWithResend}>
              <OtpDigitInput value={otp} onChange={setOtp} error={Boolean(otpError)} disabled={loading} />
              <div className={styles.otpResendColumn}>
                <button
                  type="button"
                  className={styles.resendBtn}
                  disabled={isRunning || loading}
                  onClick={handleResend}
                >
                  {modalCopy.otpResendLabel || "Resend OTP"}
                </button>
                {isRunning ? (
                  <span className={styles.resendTimer}>({String(seconds).padStart(2, "0")} sec)</span>
                ) : null}
              </div>
            </div>

            {otpError ? <p className={styles.errorText}>{otpError}</p> : null}
            {formError ? <p className={styles.errorText}>{formError}</p> : null}

            <button
              type="button"
              className={`${styles.primaryBtn} ${
                otp.length !== 4 || loading ? styles.primaryBtnDisabled : styles.primaryBtnActive
              }`}
              disabled={otp.length !== 4 || loading}
              onClick={handleVerifyAndDownload}
            >
              {loading ? modalCopy.loadingOtpLabel || "Verifying..." : modalCopy.otpSubmitLabel || "Download brochure"}
            </button>
          </div>
        ) : (
          <form className={styles.body} onSubmit={handleFormSubmit} noValidate>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={`${styles.label} ${fieldRequired("firstName", true) ? styles.required : ""}`}>
                  {fieldLabel("firstName", "First name")}
                </label>
                <input
                  className={`${styles.input} ${fieldErrors.firstName ? styles.inputError : ""}`}
                  value={form.firstName}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, firstName: sanitizeNameInput(e.target.value) }))
                  }
                  placeholder={fieldPlaceholder("firstName", "Enter first name")}
                  autoComplete={formFieldsById.firstName?.autoComplete || "given-name"}
                  disabled={loading}
                />
                {fieldErrors.firstName ? <p className={styles.errorText}>{fieldErrors.firstName}</p> : null}
              </div>
              <div className={styles.field}>
                <label className={`${styles.label} ${fieldRequired("lastName", false) ? styles.required : ""}`}>
                  {fieldLabel("lastName", "Last name")}
                </label>
                <input
                  className={`${styles.input} ${fieldErrors.lastName ? styles.inputError : ""}`}
                  value={form.lastName}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, lastName: sanitizeNameInput(e.target.value) }))
                  }
                  placeholder={fieldPlaceholder("lastName", "Enter last name")}
                  autoComplete={formFieldsById.lastName?.autoComplete || "family-name"}
                  disabled={loading}
                />
                {fieldErrors.lastName ? <p className={styles.errorText}>{fieldErrors.lastName}</p> : null}
              </div>
            </div>

            <div className={styles.field}>
              <Dropdown
                label={fieldLabel("role", "Role")}
                placeholder={fieldPlaceholder("role", "Select your role")}
                options={roleOptions}
                value={form.role}
                onChange={(val) => setForm((prev) => ({ ...prev, role: val }))}
                error={fieldErrors.role}
                required={fieldRequired("role", true)}
                searchable={false}
                size="compact"
                disabled={loading}
              />
            </div>

            <div className={styles.field}>
              <label className={`${styles.label} ${fieldRequired("schoolName", true) ? styles.required : ""}`}>
                {fieldLabel("schoolName", "School Name")}
              </label>
              <input
                className={`${styles.input} ${fieldErrors.schoolName ? styles.inputError : ""}`}
                value={form.schoolName}
                onChange={(e) => setForm((prev) => ({ ...prev, schoolName: e.target.value }))}
                placeholder={fieldPlaceholder("schoolName", "Enter school or institution name")}
                autoComplete={formFieldsById.schoolName?.autoComplete || "organization"}
                disabled={loading}
              />
              {fieldErrors.schoolName ? <p className={styles.errorText}>{fieldErrors.schoolName}</p> : null}
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <Dropdown
                  label={fieldLabel("state", "State")}
                  placeholder={loadingStates ? "Loading..." : fieldPlaceholder("state", "Select state")}
                  options={states}
                  value={form.stateName}
                  onChange={handleStateChange}
                  disabled={loadingStates || loading}
                  error={fieldErrors.state}
                  required={fieldRequired("state", true)}
                  searchable
                  size="compact"
                />
              </div>
              <div className={styles.field}>
                <Dropdown
                  label={fieldLabel("city", "City")}
                  placeholder={loadingCities ? "Loading..." : fieldPlaceholder("city", "Select city")}
                  options={cities}
                  value={form.city}
                  onChange={(val) => setForm((prev) => ({ ...prev, city: val }))}
                  disabled={!form.stateName || loadingCities || loading}
                  error={fieldErrors.city}
                  required={fieldRequired("city", true)}
                  searchable
                  size="compact"
                />
              </div>
            </div>

            <div className={styles.field}>
              <label className={`${styles.label} ${fieldRequired("email", false) ? styles.required : ""}`}>
                {fieldLabel("email", "Email")}
              </label>
              <input
                type="email"
                className={`${styles.input} ${fieldErrors.email ? styles.inputError : ""}`}
                value={form.email}
                onChange={(e) => {
                  const nextEmail = e.target.value.replace(/\s/g, "");
                  setForm((prev) => ({ ...prev, email: nextEmail }));
                  if (emailTouched) {
                    const em = validateEmailOptional(nextEmail);
                    setFieldErrors((prev) => ({
                      ...prev,
                      email: em.valid ? undefined : validationCopy.emailInvalid || em.message
                    }));
                  }
                }}
                onBlur={() => {
                  setEmailTouched(true);
                  const em = validateEmailOptional(form.email);
                  setFieldErrors((prev) => ({
                    ...prev,
                    email: em.valid ? undefined : validationCopy.emailInvalid || em.message
                  }));
                }}
                placeholder={fieldPlaceholder("email", "Enter your email")}
                autoComplete={formFieldsById.email?.autoComplete || "email"}
                disabled={loading}
              />
              {fieldErrors.email ? <p className={styles.errorText}>{fieldErrors.email}</p> : null}
            </div>

            <div className={styles.field}>
              <label className={`${styles.label} ${fieldRequired("phone", true) ? styles.required : ""}`}>
                {fieldLabel("phone", "Phone")}
              </label>
              <div className={`${styles.phoneBar} ${fieldErrors.phone ? styles.phoneBarError : ""}`}>
                <span className={styles.isdPrefix}>{phoneIsd}</span>
                <span className={styles.phoneDivider} aria-hidden />
                <input
                  type="tel"
                  inputMode="numeric"
                  className={styles.phoneInput}
                  placeholder={fieldPlaceholder("phone", "Enter mobile number")}
                  value={form.phone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setForm((prev) => ({ ...prev, phone: digits }));
                    setFieldErrors((prev) => ({ ...prev, phone: undefined }));
                    setFormError("");
                  }}
                  maxLength={10}
                  autoComplete={formFieldsById.phone?.autoComplete || "tel-national"}
                  disabled={loading}
                />
              </div>
              {fieldErrors.phone ? <p className={styles.errorText}>{fieldErrors.phone}</p> : null}
            </div>

            {formError ? <p className={styles.errorText}>{formError}</p> : null}

            <button
              type="submit"
              className={`${styles.primaryBtn} ${
                !isFormValid || loading ? styles.primaryBtnDisabled : styles.primaryBtnActive
              }`}
              disabled={!isFormValid || loading}
            >
              {loading ? modalCopy.loadingFormLabel || "Submitting..." : modalCopy.formSubmitLabel || "Submit"}
            </button>
          </form>
        )}
      </RegistrationShell>
    </div>,
    document.body
  );
};
